'use client'

import { useState, useEffect } from 'react'
import { sanityFetch } from '@utils/fetch'

import { ProjectGrid } from 'shared-components'

import type { ProjectGrid as ProjectGridType } from '@/types/sanity.types'

async function fetchInitialData(featureProjectSlug?: string) {
    return await sanityFetch({
        query: `{
            "projects": *[_type == 'project' && slug.current != null ${featureProjectSlug ? '&& slug.current != $featureProjectSlug' : ''}] | order(_createdAt desc)[0...24]{
                ...,
                "slug": slug.current,
                projectCategories[]->{
                    name,
                    "slug": slug.current,
                    title,
                    copy,
                },
                featuredImage {
                    ...,
                    asset-> {
                        url,
                        metadata {
                            lqip,
                        }
                    }
                },
            },
            "projectCategories": *[_type == 'projectCategory'] | order(name asc) {
                name,
                "slug": slug.current,
                title,
                copy,
            },
            "totalProjects": count(*[_type == 'project' && slug.current != null ${featureProjectSlug ? '&& slug.current != $featureProjectSlug' : ''}]),
        }`,
        params: featureProjectSlug ? { featureProjectSlug } : {},
        tags: [`project`],
    })
}

async function fetchMoreProjects(start: number, featureProjectSlug?: string) {
    return await sanityFetch({
        query: `{
            "projects": *[_type == 'project' && slug.current != null ${featureProjectSlug ? '&& slug.current != $featureProjectSlug' : ''}] | order(_createdAt desc)[${start}...${start + 24}]{
                ...,
                "slug": slug.current,
                projectCategories[]-> {
                    name,
                    "slug": slug.current,
                    title,
                    copy,
                },
                featuredImage {
                    ...,
                    asset-> {
                        url,
                        metadata {
                            lqip,
                        }
                    }
                },
            },
        }`,
        params: featureProjectSlug ? { featureProjectSlug } : {},
        tags: [`project`],
    })
}

async function fetchProjectsByCategory(projectCategory: string, featureProjectSlug?: string) {
    if (!projectCategory) {
        return fetchInitialData(featureProjectSlug)
    }
    return await sanityFetch({
        query: `{
            "projects": *[_type == 'project' && slug.current != null ${featureProjectSlug ? '&& slug.current != $featureProjectSlug' : ''} && $projectCategory in projectCategories[]->slug.current] | order(_createdAt desc){
                ...,
                "slug": slug.current,
                projectCategories[]-> {
                    name,
                    "slug": slug.current,
                    title,
                    copy,
                },
                featuredImage {
                    ...,
                    asset-> {
                        url,
                        metadata {
                            lqip,
                        }
                    }
                },
            },
        }`,
        params: featureProjectSlug ? { projectCategory, featureProjectSlug } : { projectCategory },
        tags: [`project`, `projectCategory:${projectCategory}`],
    })
}

type ExtendedProjectGridType = ProjectGridType & {
    featureProject: any
}

function ProjectGridCont({ title, copy, featureProject }: ExtendedProjectGridType) {
    const [projects, setProjects] = useState<any[]>([])
    const [projectCategories, setProjectCategories] = useState<any[]>([])
    const [totalProjects, setTotalProjects] = useState<number>(0)
    const [start, setStart] = useState(24)

    useEffect(() => {
        fetchInitialData(featureProject?.slug?.current).then((data) => {
            setProjects(data?.projects || [])
            setProjectCategories(data?.projectCategories || [])
            setTotalProjects(data?.totalProjects || 0)
        })
    }, [featureProject])

    const handleClick = async () => {
        const moreProjects = await fetchMoreProjects(start, featureProject?.slug?.current)
        setProjects((prevProjects) => [...prevProjects, ...moreProjects.projects])
        setStart((prevStart) => prevStart + 24)
    }

    const getProjectsByProjectCategory = async (projectCategory: string) => {
        const projectsByCategory = await fetchProjectsByCategory(projectCategory, featureProject?.slug?.current)
        setProjects(projectsByCategory.projects)
    }

    return (
        <ProjectGrid
            title={title}
            copy={copy}
            projects={projects}
            totalProjects={totalProjects}
            featureProject={featureProject}
            projectCategories={projectCategories}
            showMore={handleClick}
            getProjectsByProjectCategory={getProjectsByProjectCategory}
        />
    )
}

export default ProjectGridCont
